import { useState, useEffect, useMemo } from "react";
import type {
  GazePoint,
  ScanpathData,
  FixationDetectionConfig,
  Fixation,
  Saccade,
} from "../types/gazeHeatmapTypes";
import { generateScanpath, getFixationsAtTime } from "../utils/fixationDetection";

interface UseScanpathGenerationParams {
  /** Gaze points to analyze */
  points: GazePoint[];
  /** Fixation detection configuration */
  config?: Partial<FixationDetectionConfig>;
  /** Scene rotation for UV conversion (Y axis) - unused, kept for compatibility */
  sceneRotationY?: number;
  /** Whether to generate the scanpath */
  enabled?: boolean;
  /** Whether this is video 360 content (uses AVProVideo shader formula) */
  isVideo360?: boolean;
}

interface UseScanpathGenerationResult {
  /** Generated scanpath data */
  scanpath: ScanpathData | null;
  /** Whether the scanpath is being generated */
  isGenerating: boolean;
  /** Error message if generation failed */
  error: string | null;
  /** Regenerate the scanpath */
  regenerate: () => void;
}

/**
 * Hook to generate scanpath from gaze points
 */
export function useScanpathGeneration({
  points,
  config = {},
  sceneRotationY = 0,
  enabled = true,
  isVideo360 = false,
}: UseScanpathGenerationParams): UseScanpathGenerationResult {
  const [scanpath, setScanpath] = useState<ScanpathData | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [regenerateKey, setRegenerateKey] = useState(0);

  // Memoize config
  const memoizedConfig = useMemo(() => config, [JSON.stringify(config)]);

  useEffect(() => {
    if (!enabled || points.length === 0) {
      setScanpath(null);
      setIsGenerating(false);
      return undefined;
    }

    setIsGenerating(true);
    setError(null);

    // Use requestAnimationFrame to avoid blocking UI
    const rafId = requestAnimationFrame(() => {
      try {
        const result = generateScanpath(points, memoizedConfig, sceneRotationY, isVideo360);
        setScanpath(result);
        setIsGenerating(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to generate scanpath");
        setScanpath(null);
        setIsGenerating(false);
      }
    });

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [points, memoizedConfig, sceneRotationY, enabled, regenerateKey, isVideo360]);

  return {
    scanpath,
    isGenerating,
    error,
    regenerate: () => setRegenerateKey((k) => k + 1),
  };
}

interface UseVideoSyncedScanpathParams {
  /** Full scanpath data */
  scanpath: ScanpathData | null;
  /** Current video time in seconds */
  currentTime: number;
  /** How many seconds of history to show */
  historyDuration?: number;
  /** Whether to sync */
  enabled?: boolean;
}

interface UseVideoSyncedScanpathResult {
  /** Fixations visible at current time */
  visibleFixations: Fixation[];
  /** Saccades visible at current time */
  visibleSaccades: Saccade[];
  /** Current fixation (if gaze is in a fixation at this moment) */
  currentFixation: Fixation | null;
}

/**
 * Hook to get scanpath elements visible at the current video time
 */
export function useVideoSyncedScanpath({
  scanpath,
  currentTime,
  historyDuration = 5,
  enabled = true,
}: UseVideoSyncedScanpathParams): UseVideoSyncedScanpathResult {
  const { visibleFixations, visibleSaccades, currentFixation } = useMemo(() => {
    if (!enabled || !scanpath) {
      return {
        visibleFixations: [] as Fixation[],
        visibleSaccades: [] as Saccade[],
        currentFixation: null as Fixation | null,
      };
    }

    const { fixations, saccades } = getFixationsAtTime(scanpath, currentTime, historyDuration);

    // Find current fixation (gaze is inside this fixation right now)
    const current =
      fixations.find((f) => f.startTime <= currentTime && f.endTime >= currentTime) || null;

    return {
      visibleFixations: fixations,
      visibleSaccades: saccades,
      currentFixation: current,
    };
  }, [scanpath, currentTime, historyDuration, enabled]);

  return {
    visibleFixations,
    visibleSaccades,
    currentFixation,
  };
}
